/*
 * Wrapper functions for CookieStore using document.cookie as a fallback if not supported
 */

export const getCookieMap = (cookieString = document.cookie) => {
  // document.cookie has the format: "key1=value1;  key2=value2;  key3=value3"
  return cookieString.split(";").reduce((result: Map<string, string>, next: string) => {
    const cookieKeyValuePair = next.trim(); // " key=value " => "key=value"
    const [key, ...restOfString] = cookieKeyValuePair.split("="); // ["key", ["value"]]
    const value = decodeURIComponent(restOfString.join("=")); // .join() handles if value contained "="
    return result.set(key, value);
  }, new Map<string, string>());
};

export const getCookieValue = (name: string): string | undefined => {
  // New Async API, not supported in FF/Safari:
  // if (window.cookieStore) {
  //   return (await window.cookieStore.get(name))?.value;
  // }
  return getCookieMap().get(name);
};

export const setCookieValue = (options: CookieInit): undefined => {
  // New Async API, not supported in FF/Safari:
  // if (window.cookieStore) {
  //   const { domain, ...rest } = options;
  //   const validatedDomain = domain === "localhost" ? undefined : domain;
  //   return await window.cookieStore.set({ ...rest, domain: validatedDomain });
  // }
  // https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie#write_a_new_cookie
  const { name, value, expires, ...rest } = options;
  const encodedValue = encodeURIComponent(value);
  const base = `${name}=${encodedValue}`;
  const expiration = expires != null ? ` ;expires=${new Date(expires).toUTCString()}` : "";
  const modifiers = Object.entries(rest)
    .map(([key, val]) => (val != null ? ` ;${key}=${val}` : ""))
    .join("");
  document.cookie = base + modifiers + expiration;
  return;
};

export const deleteCookieValue = (options: { name: string; domain?: string; path?: string }): undefined => {
  return setCookieValue({ ...options, value: "", expires: -1 });
};

/*
 * CookieStore API Types
 * https://developer.mozilla.org/en-US/docs/Web/API/CookieStore#browser_compatibility
 */

interface CookieListItem {
  name: string;
  value: string;
  path: string;
  domain: string | null;
  expires: number | null; // timestamp, unix time in ms
  secure: boolean;
  sameSite: "strict" | "lax" | "none";
}

interface CookieInit {
  name: string;
  value: string;
  path?: string;
  domain?: string;
  expires?: number; // timestamp, unix time in ms
  sameSite?: "strict" | "lax" | "none";
}

// https://wicg.github.io/cookie-store/#CookieChangeEvent
export interface CookieChangeEvent extends Event {
  readonly changed: CookieListItem[];
  readonly deleted: CookieListItem[];
  type: "deleted" | "changed";
}

type SetCookie = {
  (options: CookieInit): Promise<undefined>;
  (name: string, value: string): Promise<undefined>;
};

// https://wicg.github.io/cookie-store/#CookieStore
interface CookieStore extends EventTarget {
  get: (name: string | { name: string; url: string }) => Promise<CookieListItem | undefined>;
  getAll: (name: string | { name: string; url: string }) => Promise<CookieListItem[]>;
  set: SetCookie; // function overload for multiple signatures
  delete: (name: string | { name: string; domain?: string | null; path?: string }) => Promise<undefined>;
  onchange: (event: CookieChangeEvent) => boolean | void | Promise<boolean | void>;
}

declare global {
  interface Window {
    cookieStore: CookieStore;
  }
}
